import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Ad, AppContent, ErrorBoundary } from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getBy,
  getShouldHideAuthorImage,
  getUpdatedText,
  getCover,
  getCreatedAt,
  getCreatedAtISO,
  getFiniteScrollArticles,
  getImageCover,
  getMd5hashPostCanonicalUrl,
  getMetadataDescription,
  getNoIndex,
  getOgDescription,
  getOgImage,
  getOgTitle,
  getOpenWebId,
  getSeoDescription,
  getSeoTitle,
  getShareConfig,
  getShowRecommendationsAdvertisement,
  getSlideshowData,
  getSponsor,
  getTags,
  getTitle,
  getTwitterDescription,
  getTwitterImage,
  getTwitterTitle,
  getUpdatedAt,
  getUpdatedAtISO,
  getShowUpdatedAt,
  getOutbrainWidgetID,
  getOutbrainFiniteScrollWidgetID,
  getDisclaimerText,
  getShouldDisplayDisclaimerAtBottom,
  getCommercialTags,
  getBreadCrumbs,
  getLoadingArticles,
  getFaqData,
  getArticleId,
  getIntro,
  getShouldShowHeroImage,
  getOwnerUsername,
} from '../../store/template/postPage/postPagePrague/postPagePrague.selectors';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getSiteName,
  getTemplateName,
  getExperiments,
  getResourceID,
  getIsMobileViewer,
} from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { PragueArticleComponent } from './PragueArticleComponent';
import { FiniteScroll } from '../../components/finiteScroll/FiniteScroll';
import 'iframe-resizer/js/iframeResizer.contentWindow.min';
import { PostPageDataType } from '../../store/template/postPage/types';
import { getFiniteScrollArticlesURLs } from '../../store/template/postPage/postPageBali/postPageBali.selectors';
import { getCookieByName } from '../templates.utils';

interface PostPagePragueProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const PostPagePragueComponent: React.FunctionComponent<PostPagePragueProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPagePragueContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const getOutbrainWidgetIDForTest = (widgetID: string, experiments?: string[]) => {
  if (experiments?.includes('outbrain-app-widget|no-outbrain')) return '';
  if (experiments?.includes('outbrain-app-widget|ar-5')) return 'AR_5';
  if (experiments?.includes('outbrain-app-widget|ar-8')) return 'AR_8';
  if (experiments?.includes('outbrain-app-widget|ar-9')) return 'AR_9';
  if (experiments?.includes('outbrain-app-widget|ar-1')) return 'AR_1';
  if (experiments?.includes('outbrain-app-widget|ar-10')) return 'AR_10';
  if (experiments?.includes('outbrain-app-widget|ar-12')) return 'AR_12';
  return widgetID;
};

export const PostPagePragueContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const title = useSelector(getTitle);
  const intro = useSelector(getIntro);
  const by = useSelector(getBy);
  const shouldHideAuthorImage = useSelector(getShouldHideAuthorImage);
  const updatedText = useSelector(getUpdatedText);
  const authors = useSelector(getAuthors);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const outbrainWidgetID = useSelector(getOutbrainWidgetID);
  const outbrainFiniteScrollWidgetID = useSelector(getOutbrainFiniteScrollWidgetID);
  const breadCrumbs = useSelector(getBreadCrumbs);
  const openWebId = useSelector(getOpenWebId);
  const tags = useSelector(getTags);
  const pageDescription = useSelector(getMetadataDescription);
  const seoTitle = useSelector(getSeoTitle) || title;
  const pageImage = useSelector(getImageCover);
  const createdAt = useSelector(getCreatedAt);
  const createdAtISO = useSelector(getCreatedAtISO);
  const updatedAt = useSelector(getUpdatedAt);
  const updatedAtISO = useSelector(getUpdatedAtISO);
  const showUpdatedAt = useSelector(getShowUpdatedAt);
  const noIndex = useSelector(getNoIndex);
  const ogDescription = useSelector(getOgDescription);
  const ogTitle = useSelector(getOgTitle);
  const ogImage = useSelector(getOgImage);
  const twitterDescription = useSelector(getTwitterDescription);
  const twitterTitle = useSelector(getTwitterTitle);
  const twitterImage = useSelector(getTwitterImage);
  const seoDescription = useSelector(getSeoDescription);
  const articleSection = breadCrumbs ? breadCrumbs.categoryDisplayName : '';
  const slideshow = useSelector(getSlideshowData);
  const md5hashPostCanonicalUrl = useSelector(getMd5hashPostCanonicalUrl);
  const finiteScrollArticles = useSelector(getFiniteScrollArticles);
  const articlesUrls = useSelector(getFiniteScrollArticlesURLs);
  const loadingArticles = useSelector(getLoadingArticles);
  const disclaimerText = useSelector(getDisclaimerText);
  const shouldDisplayDisclaimerAtBottom = useSelector(getShouldDisplayDisclaimerAtBottom);
  const commercialTags = useSelector(getCommercialTags);
  const experiments = useSelector(getExperiments);
  const faqData = useSelector(getFaqData);
  const articleId = useSelector(getArticleId);
  const shouldShowHeroImage = useSelector(getShouldShowHeroImage);
  const ownerUsername = useSelector(getOwnerUsername);
  const resourceID = useSelector(getResourceID);
  const isMobileViewer = useSelector(getIsMobileViewer);
  const [mainArticleURL, setMainArticleURL] = React.useState('');

  const postPageData: PostPageDataType = useMemo(() => ({
    slideshowData: slideshow,
    commercialTags,
    updatedText,
    disclaimerText,
    showUpdatedAt,
  }), [slideshow, commercialTags, updatedText, disclaimerText, showUpdatedAt]);

  // Outbrain Widget Experiment
  const outbrainWidgetIDForTest = getOutbrainWidgetIDForTest(outbrainWidgetID, experiments);

  React.useEffect(() => {
    const sessionId = getCookieByName('mm-session-id');
     // @ts-ignore TS2339
     window.OB_extIdSecondary = window.OB_extIdSecondary || [];
     // @ts-ignore TS2339
     window.OB_extIdSecondary = articleId;
    // @ts-ignore TS2339
    window.OB_extId = window.OB_extId || [];
    // @ts-ignore TS2339
    window.OB_extId = experiments.join() || 'none_experiment';
    // @ts-ignore TS2339
    window.OB_pubImpId = sessionId;
  }, [articleId, experiments]);

  React.useEffect(() => {
    if (!mainArticleURL && window) {
      setMainArticleURL(window.location.href);
    }
  }, [mainArticleURL]);

  return (
    <React.Fragment>
      <PostMetaTags {...{
        pageDescription,
        seoTitle,
        pageImage,
        createdAtISO,
        updatedAtISO,
        noIndex,
        ogDescription,
        ogTitle,
        ogImage,
        twitterDescription,
        twitterTitle,
        twitterImage,
        seoDescription,
        articleSection,
        authors,
        prevSlideshowUrl: slideshow && slideshow.prevLink,
        nextSlideshowUrl: slideshow && slideshow.nextLink,
        tags,
        breadCrumbs,
        faqData,
      }}
      />
      <HeadScripts title={title} tags={tags} />
      <Theme>
        <AppContent>
          <Ad id={topAdId} onSuccess={topAdOnSuccess} />
          <FiniteScroll
            articles={finiteScrollArticles}
            articleComponent={PragueArticleComponent}
            articlesUrls={articlesUrls}
            loadingArticles={loadingArticles}
            postPageData={postPageData}
            by={by}
            siteName={siteName}
            templateName={templateName}
            outbrainWidgetID={outbrainFiniteScrollWidgetID}
            openWebId={openWebId}
            shouldDisplayDisclaimerAtBottom={shouldDisplayDisclaimerAtBottom}
            experiment={experiments.join()}
            isMobileViewer={isMobileViewer}
          >
            <PragueArticleComponent {...{
              breadCrumbs,
              title,
              intro,
              by,
              shouldHideAuthorImage,
              updatedText,
              updatedAt,
              updatedAtISO,
              showUpdatedAt,
              authors,
              shareConfig,
              canonical,
              cover,
              siteName,
              templateName,
              sponsor,
              slideshow,
              body,
              sideBar1Id,
              sideBar2Id,
              sideBar2AdOnSuccess,
              sideBar1AdOnSuccess,
              showRecommendationsAdvertisement,
              outbrainWidgetID: outbrainWidgetIDForTest,
              createdAt,
              createdAtISO,
              tags,
              md5hashPostCanonicalUrl,
              openWebId,
              mainArticleURL,
              disclaimerText,
              shouldDisplayDisclaimerAtBottom,
              addMMPlayerPlaceholder: false,
              addOpenwebLiveblogPlaceholder: true,
              experiments,
              commercialTags,
              shouldShowHeroImage,
              ownerUsername,
              resourceID,
              isMobileViewer,
            }}
            />
          </FiniteScroll>
          <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
